import { useCallback, useEffect, useState } from 'react';
import { supabase } from './supabase';

/**
 * SLA policies per team × priority. Response + resolution targets in hours,
 * optionally measured against a business-hours calendar (Mon–Fri, 09:00–17:00).
 * Persisted in Supabase `public.sla_policies`.
 */
export type Priority = 'P1' | 'P2' | 'P3' | 'P4';
export type CalendarMode = '24x7' | 'business';
export type SlaTargets = { responseH: number; resolutionH: number };
export type SlaPolicy = {
  calendar: CalendarMode;
  targets: Record<Priority, SlaTargets>;
};

export const DEFAULT_POLICY: SlaPolicy = {
  calendar: '24x7',
  targets: {
    P1: { responseH: 1, resolutionH: 4 },
    P2: { responseH: 2, resolutionH: 8 },
    P3: { responseH: 4, resolutionH: 24 },
    P4: { responseH: 8, resolutionH: 72 },
  },
};

const WORK_START = 9;
const WORK_END = 17; // 8 working hours / weekday

function nextWorkStart(d: Date): Date {
  const x = new Date(d);
  const hour = x.getHours() + x.getMinutes() / 60;
  if (x.getDay() !== 0 && x.getDay() !== 6 && hour < WORK_START) {
    x.setHours(WORK_START, 0, 0, 0);
    return x;
  }
  x.setDate(x.getDate() + 1);
  x.setHours(WORK_START, 0, 0, 0);
  while (x.getDay() === 0 || x.getDay() === 6) x.setDate(x.getDate() + 1);
  return x;
}

/** Add working hours within Mon–Fri 09:00–17:00. */
function addWorkingHours(startMs: number, hours: number): number {
  let remaining = hours * 3_600_000;
  let d = new Date(startMs);
  let guard = 0;
  while (remaining > 0 && guard++ < 5000) {
    const hour = d.getHours() + d.getMinutes() / 60;
    if (d.getDay() === 0 || d.getDay() === 6 || hour < WORK_START || hour >= WORK_END) {
      d = nextWorkStart(d);
      continue;
    }
    const endOfDay = new Date(d);
    endOfDay.setHours(WORK_END, 0, 0, 0);
    const avail = endOfDay.getTime() - d.getTime();
    if (avail >= remaining) return d.getTime() + remaining;
    remaining -= avail;
    d = new Date(endOfDay.getTime());
  }
  return d.getTime();
}

export function computeDue(
  policy: SlaPolicy,
  priority: string,
  fromMs: number,
): { responseDueAt: number; resolutionDueAt: number } {
  const pKey = priority === 'Critical' || priority === 'Urgent' ? 'P1'
    : priority === 'High' ? 'P2'
    : priority === 'Medium' ? 'P3'
    : priority === 'Low' ? 'P4'
    : (priority as Priority);

  const t = policy.targets[pKey as Priority] ?? DEFAULT_POLICY.targets.P3;
  const add =
    policy.calendar === 'business'
      ? addWorkingHours
      : (s: number, h: number) => s + h * 3_600_000;
  return { responseDueAt: add(fromMs, t.responseH), resolutionDueAt: add(fromMs, t.resolutionH) };
}

export function useSlaPolicy(_teamId: string) {
  const [policy, setPolicy] = useState<SlaPolicy>(DEFAULT_POLICY);

  const loadPolicy = useCallback(async () => {
    if (!supabase) return;
    try {
      const { data, error } = await supabase.from('sla_policies').select('*').eq('is_active', true);
      if (error || !data || data.length === 0) return;

      const targets: Record<Priority, SlaTargets> = { ...DEFAULT_POLICY.targets };
      for (const row of data) {
        const p = row.target_priority;
        const key: Priority | null = p === 'Urgent' || p === 'Critical' || p === 'P1' ? 'P1'
          : p === 'High' || p === 'P2' ? 'P2'
          : p === 'Medium' || p === 'P3' ? 'P3'
          : p === 'Low' || p === 'P4' ? 'P4' : null;
        if (key) {
          targets[key] = {
            responseH: Number(row.first_response_hours) || targets[key].responseH,
            resolutionH: Number(row.resolution_hours) || targets[key].resolutionH,
          };
        }
      }
      setPolicy({ calendar: '24x7', targets });
    } catch (err) {
      console.error('Failed to load SLA policies from Supabase:', err);
    }
  }, []);

  useEffect(() => {
    loadPolicy();
  }, [loadPolicy]);

  const save = useCallback(
    async (next: SlaPolicy) => {
      setPolicy(next);
      if (!supabase) return;
      try {
        const prioMap: Record<Priority, string> = { P1: 'Urgent', P2: 'High', P3: 'Medium', P4: 'Low' };
        for (const [prioKey, target] of Object.entries(next.targets)) {
          const dbPrio = prioMap[prioKey as Priority];
          await supabase
            .from('sla_policies')
            .update({
              first_response_hours: target.responseH,
              resolution_hours: target.resolutionH,
            })
            .eq('target_priority', dbPrio);
        }
      } catch (err) {
        console.error('Failed to save SLA policy to Supabase:', err);
      }
    },
    [],
  );

  return { policy, save };
}
